// Cross-page applicant profile sync.
//
// Quick Check (/admission) and Advanced Wizard (/advanced) both ask
// for the same grade + subject + test scores. Before this helper,
// users had to re-type them if they moved between pages. Now Quick
// Check writes the applicant fields to sessionStorage on submit and
// Advanced Wizard hydrates from that snapshot on mount.
//
// Deliberately sessionStorage (not localStorage) — the applicant
// profile is tab-scoped so a family member on the same laptop doesn't
// see the last user's grades. sessionStorage clears on tab close.

const KEY = "wua:applicant-profile";

// Fields shared between the two forms. Anything Quick-Check-only
// (native_english, applying_to_us, sat_act_status, english_test_status)
// is deliberately excluded — those are ephemeral UI toggles that
// don't translate cleanly to AdvancedWizard.
const SHARED_FIELDS = [
  "curriculum",
  "a_level", "ib", "cbse", "american_diploma", "gpa", "atar", "isc",
  "ap_composite", "abitur", "french_bac", "hkdse", "suneung",
  "a_level_maths", "a_level_english", "ib_maths", "ib_english",
  "sat", "act", "ielts", "toefl",
  "activities_strength",
  "intended_degrees",
];

// `explicit` = fields the student actually entered or confirmed; the rest are form defaults.
export function saveApplicantProfile(form, explicit = []) {
  if (typeof window === "undefined") return;
  try {
    const snapshot = {};
    for (const k of SHARED_FIELDS) {
      if (form[k] !== undefined) snapshot[k] = form[k];
    }
    snapshot.__explicit = explicit.filter((k) => SHARED_FIELDS.includes(k));
    snapshot.__saved_at = new Date().toISOString();
    sessionStorage.setItem(KEY, JSON.stringify(snapshot));
  } catch (_) {
    // sessionStorage may be disabled (Safari private mode etc) — the
    // sync is a nice-to-have, so we fail silently rather than break
    // the submit flow.
  }
}

export function readApplicantProfile() {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Return only the shared fields — future callers shouldn't rely on
    // internal book-keeping keys like `__saved_at`.
    const out = {};
    for (const k of SHARED_FIELDS) {
      if (parsed[k] !== undefined) out[k] = parsed[k];
    }
    return Object.keys(out).length > 0 ? out : null;
  } catch (_) {
    return null;
  }
}

export function readApplicantExplicit() {
  if (typeof window === "undefined") return [];
  try {
    const parsed = JSON.parse(sessionStorage.getItem(KEY) || "{}");
    return Array.isArray(parsed.__explicit) ? parsed.__explicit : [];
  } catch (_) {
    return [];
  }
}

export function clearApplicantProfile() {
  if (typeof window === "undefined") return;
  try { sessionStorage.removeItem(KEY); } catch (_) { /* noop */ }
}
